import React, { useEffect, useMemo, useState } from "react";
import {
  FiX,
  FiClock,
  FiBookOpen,
  FiBarChart2,
  FiCheckCircle,
  FiExternalLink,
} from "react-icons/fi";
import {
  collection,
  getDocs,
  orderBy,
  query,
  Timestamp,
  where,
} from "firebase/firestore";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";
import { db } from "../../../firebase/firebaseConfig";

ChartJS.register(
  BarElement,
  CategoryScale,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip
);

function getStudentId(student) {
  return student?.uid || student?.userId || student?.id || "";
}

function toDate(value) {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  if (value instanceof Date) return value;
  return null;
}

function formatDate(value) {
  const d = toDate(value);
  if (!d) return "--";
  return d.toLocaleString("vi-VN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatShortDate(value) {
  const d = toDate(value);
  if (!d) return "--";
  return d.toLocaleDateString("vi-VN", {
    day: "2-digit",
    month: "2-digit",
  });
}

function getTimeMs(value) {
  const d = toDate(value);
  return d ? d.getTime() : 0;
}

function getPercent(item) {
  if (typeof item?.percent === "number" && Number.isFinite(item.percent)) {
    return Math.round(item.percent);
  }

  const correct = Number(item?.score?.correct ?? item?.correct);
  const total = Number(item?.score?.total ?? item?.total);
  if (Number.isFinite(correct) && Number.isFinite(total) && total > 0) {
    return Math.round((correct / total) * 100);
  }

  return 0;
}

function getDurationSeconds(item) {
  const value = item?.duration ?? item?.timeSpent ?? item?.elapsedSeconds;
  const seconds = Number(value);
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  return Math.round(seconds);
}

function formatDuration(seconds, fallback = "--") {
  if (typeof seconds !== "number" || !Number.isFinite(seconds) || seconds <= 0) {
    return fallback;
  }

  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins >= 60) {
    const hours = Math.floor(mins / 60);
    const remMins = mins % 60;
    return remMins ? `${hours}h ${remMins}'` : `${hours}h`;
  }
  if (mins <= 0) return `${secs}s`;
  return secs ? `${mins}' ${secs}s` : `${mins}'`;
}

function getAttemptKind(item) {
  const isAssigned =
    item.isAssigned ||
    item.type === "assignment" ||
    item.source === "assignment";
  return isAssigned ? "Bài được giao" : "Tự luyện";
}

function getStartDate(range, customFrom) {
  const now = new Date();

  if (range === "7d") {
    const d = new Date(now);
    d.setDate(d.getDate() - 7);
    return d;
  }

  if (range === "30d") {
    const d = new Date(now);
    d.setDate(d.getDate() - 30);
    return d;
  }

  if (range === "90d") {
    const d = new Date(now);
    d.setDate(d.getDate() - 90);
    return d;
  }

  if (range === "custom" && customFrom) {
    return new Date(`${customFrom}T00:00:00`);
  }

  return null;
}

function getEndDate(range, customTo) {
  if (range === "custom" && customTo) {
    return new Date(`${customTo}T23:59:59`);
  }

  return null;
}

function StatCard({ icon, label, value, sub }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-medium text-slate-500">{label}</p>
          <p className="mt-1 text-2xl font-bold text-slate-900">{value}</p>
          {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
        </div>
        <div className="rounded-xl bg-blue-50 p-2 text-blue-600">{icon}</div>
      </div>
    </div>
  );
}

function ChartPanel({ title, children, empty }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <h3 className="font-bold text-slate-900">{title}</h3>
      <div className="mt-4 h-72">
        {empty ? (
          <div className="flex h-full items-center justify-center rounded-xl border border-dashed border-slate-200 text-sm text-slate-500">
            Chưa đủ dữ liệu để vẽ biểu đồ.
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}

export default function StudentHistoryDrawer({
  open,
  onClose,
  student,
  classId,
}) {
  const [range, setRange] = useState("30d");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [histories, setHistories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [historyType, setHistoryType] = useState("all");

  const studentId = getStudentId(student);

  useEffect(() => {
    if (!open || !studentId) return;

    const fetchHistories = async () => {
      setLoading(true);

      try {
        const constraints = [
          where("userId", "==", studentId),
          orderBy("submittedAt", "desc"),
        ];

        const fromDate = getStartDate(range, customFrom);
        const toDateValue = getEndDate(range, customTo);

        if (fromDate) {
          constraints.unshift(
            where("submittedAt", ">=", Timestamp.fromDate(fromDate))
          );
        }

        if (toDateValue) {
          constraints.unshift(
            where("submittedAt", "<=", Timestamp.fromDate(toDateValue))
          );
        }

        const q = query(collection(db, "test_histories"), ...constraints);
        const snap = await getDocs(q);

        const rows = snap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setHistories(rows);
      } catch (err) {
        console.error("Cannot load test histories:", err);
        setHistories([]);
      } finally {
        setLoading(false);
      }
    };

    fetchHistories();
  }, [open, studentId, range, customFrom, customTo]);

  const filteredHistories = useMemo(() => {
    return histories.filter((item) => {
      const isAssigned =
        item.isAssigned ||
        item.type === "assignment" ||
        item.source === "assignment";

      const matchClass = !classId || !isAssigned || item.classId === classId;

      const matchType =
        historyType === "all" ||
        (historyType === "assignment" && isAssigned) ||
        (historyType === "practice" && !isAssigned);

      return matchClass && matchType;
    });
  }, [histories, classId, historyType]);

  const stats = useMemo(() => {
    const total = filteredHistories.length;
    const assignment = filteredHistories.filter(
      (x) => x.isAssigned || x.type === "assignment" || x.source === "assignment"
    ).length;

    const practice = total - assignment;
    const durations = filteredHistories
      .map(getDurationSeconds)
      .filter((x) => typeof x === "number");

    const avg =
      total > 0
        ? Math.round(
            filteredHistories.reduce((sum, x) => sum + getPercent(x), 0) /
              total
          )
        : 0;

    const best =
      total > 0
        ? Math.max(...filteredHistories.map((x) => getPercent(x)))
        : 0;

    const avgDuration =
      durations.length > 0
        ? Math.round(durations.reduce((sum, x) => sum + x, 0) / durations.length)
        : null;
    const totalDuration = durations.reduce((sum, x) => sum + x, 0);
    const fastest = durations.length ? Math.min(...durations) : null;

    return {
      total,
      assignment,
      practice,
      avg,
      best,
      avgDuration,
      totalDuration,
      fastest,
    };
  }, [filteredHistories]);

  const chartRows = useMemo(() => {
    return filteredHistories
      .map((item) => {
        const duration = getDurationSeconds(item);
        return {
          id: item.id,
          title: item.title || "Untitled test",
          label: formatShortDate(item.submittedAt || item.createdAt),
          percent: getPercent(item),
          durationMinutes: duration ? Math.round((duration / 60) * 10) / 10 : null,
          kind: getAttemptKind(item),
          submittedMs: getTimeMs(item.submittedAt || item.createdAt),
        };
      })
      .sort((a, b) => a.submittedMs - b.submittedMs);
  }, [filteredHistories]);

  const trendChartData = useMemo(() => {
    const rows = chartRows.slice(-12);
    return {
      labels: rows.map((row, index) => `${index + 1}. ${row.label}`),
      datasets: [
        {
          label: "Điểm (%)",
          data: rows.map((row) => row.percent),
          borderColor: "#2563eb",
          backgroundColor: "#2563eb",
          tension: 0.3,
          yAxisID: "score",
        },
        {
          label: "Thời gian (phút)",
          data: rows.map((row) => row.durationMinutes),
          borderColor: "#f59e0b",
          backgroundColor: "#f59e0b",
          tension: 0.3,
          yAxisID: "duration",
        },
      ],
    };
  }, [chartRows]);

  const trendChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { position: "bottom" },
        tooltip: {
          callbacks: {
            afterBody: (items) => {
              const row = chartRows.slice(-12)[items[0]?.dataIndex];
              return row ? [`${row.kind}`, row.title] : [];
            },
          },
        },
      },
      scales: {
        score: {
          beginAtZero: true,
          max: 100,
          ticks: { callback: (value) => `${value}%` },
        },
        duration: {
          beginAtZero: true,
          position: "right",
          grid: { drawOnChartArea: false },
          ticks: { callback: (value) => `${value}'` },
        },
      },
    }),
    [chartRows]
  );

  const durationBarData = useMemo(() => {
    const rows = chartRows.filter((row) => row.durationMinutes !== null).slice(-10);
    return {
      labels: rows.map((row, index) => `${index + 1}. ${row.label}`),
      datasets: [
        {
          label: "Thời gian làm bài (phút)",
          data: rows.map((row) => row.durationMinutes),
          backgroundColor: rows.map((row) =>
            row.kind === "Bài được giao" ? "#2563eb" : "#10b981"
          ),
          borderRadius: 6,
        },
      ],
    };
  }, [chartRows]);

  const durationBarOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            afterBody: (items) => {
              const rows = chartRows.filter((row) => row.durationMinutes !== null).slice(-10);
              const row = rows[items[0]?.dataIndex];
              return row ? [`${row.kind}`, row.title] : [];
            },
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: { callback: (value) => `${value}'` },
        },
      },
    }),
    [chartRows]
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />

      <div className="absolute right-0 top-0 h-full w-full max-w-5xl overflow-y-auto bg-slate-50 shadow-2xl">
        <div className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 px-5 py-4 backdrop-blur">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-slate-900">
                Lịch sử làm bài
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                {student?.displayName ||
                  student?.name ||
                  student?.fullName ||
                  student?.email ||
                  "Học viên"}
              </p>
            </div>

            <button
              onClick={onClose}
              className="rounded-xl border border-slate-200 p-2 text-slate-500 hover:bg-slate-100"
            >
              <FiX size={20} />
            </button>
          </div>

          <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center">
            <select
              value={range}
              onChange={(e) => setRange(e.target.value)}
              className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500"
            >
              <option value="7d">7 ngày gần nhất</option>
              <option value="30d">30 ngày gần nhất</option>
              <option value="90d">90 ngày gần nhất</option>
              <option value="all">Toàn bộ</option>
              <option value="custom">Tuỳ chọn</option>
            </select>
            <select
  value={historyType}
  onChange={(e) => setHistoryType(e.target.value)}
  className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500"
>
  <option value="all">Tất cả bài làm</option>
  <option value="assignment">Bài được giao</option>
  <option value="practice">Bài tự luyện</option>
</select>

            {range === "custom" && (
              <div className="flex flex-col gap-2 sm:flex-row">
                <input
                  type="date"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
                />
                <input
                  type="date"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
                />
              </div>
            )}
          </div>
        </div>

        <div className="space-y-5 p-5">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-7">
            <StatCard
              icon={<FiBookOpen />}
              label="Tổng bài"
              value={stats.total}
              sub="Bài đã nộp"
            />
            <StatCard
              icon={<FiBarChart2 />}
              label="Điểm TB"
              value={`${stats.avg}%`}
              sub="Theo phần trăm"
            />
            <StatCard
              icon={<FiCheckCircle />}
              label="Cao nhất"
              value={`${stats.best}%`}
              sub="Bài tốt nhất"
            />
            <StatCard
              icon={<FiClock />}
              label="Bài được giao"
              value={stats.assignment}
              sub="Assignment"
            />
            <StatCard
              icon={<FiBookOpen />}
              label="Tự luyện"
              value={stats.practice}
              sub="Self-practice"
            />
            <StatCard
              icon={<FiClock />}
              label="TG trung bình"
              value={formatDuration(stats.avgDuration)}
              sub="Mỗi lượt nộp"
            />
            <StatCard
              icon={<FiClock />}
              label="Tổng thời gian"
              value={formatDuration(stats.totalDuration)}
              sub={stats.fastest ? `Nhanh nhất: ${formatDuration(stats.fastest)}` : "Đã ghi nhận"}
            />
          </div>

          <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
            <ChartPanel title="Xu hướng điểm và thời gian" empty={chartRows.length < 2}>
              <Line data={trendChartData} options={trendChartOptions} />
            </ChartPanel>
            <ChartPanel
              title="Thời gian làm theo bài gần đây"
              empty={chartRows.filter((row) => row.durationMinutes !== null).length === 0}
            >
              <Bar data={durationBarData} options={durationBarOptions} />
            </ChartPanel>
          </div>

          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 px-5 py-4">
              <h3 className="font-bold text-slate-900">Danh sách bài đã làm</h3>
              <p className="mt-1 text-sm text-slate-500">
                Bao gồm bài được giao và bài học viên tự làm.
              </p>
            </div>

            {loading ? (
              <div className="p-8 text-center text-slate-500">
                Đang tải lịch sử...
              </div>
            ) : filteredHistories.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                Chưa có lịch sử làm bài trong khoảng thời gian này.
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {filteredHistories.map((item) => {
                  const isAssigned =
                    item.isAssigned ||
                    item.type === "assignment" ||
                    item.source === "assignment";

                  return (
                    <div
                      key={item.id}
                      className="grid gap-4 px-5 py-4 hover:bg-slate-50 lg:grid-cols-[1.7fr_0.7fr_0.7fr_0.8fr_0.8fr_0.5fr]"
                    >
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <h4 className="font-semibold text-slate-900">
                            {item.title || "Untitled test"}
                          </h4>
                          <span
                            className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                              isAssigned
                                ? "bg-blue-50 text-blue-700"
                                : "bg-emerald-50 text-emerald-700"
                            }`}
                          >
                            {isAssigned ? "Bài được giao" : "Tự luyện"}
                          </span>
                        </div>

                        <p className="mt-1 text-sm text-slate-500">
                          {formatDate(item.submittedAt || item.createdAt)}
                        </p>

                        {item.assignmentId && (
                          <p className="mt-1 text-xs text-slate-400">
                            Assignment ID: {item.assignmentId}
                          </p>
                        )}
                      </div>

                      <div>
                        <p className="text-xs text-slate-500">Điểm</p>
                        <p className="font-bold text-slate-900">
                          {item?.score?.correct ?? item.correct ?? 0}/
                          {item?.score?.total ?? item.total ?? 0}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs text-slate-500">Phần trăm</p>
                        <p
                          className={`font-bold ${
                            Number(item.percent || 0) >= 70
                              ? "text-emerald-600"
                              : Number(item.percent || 0) >= 50
                              ? "text-amber-600"
                              : "text-red-600"
                          }`}
                        >
                          {item.percent || 0}%
                        </p>
                      </div>

                      <div>
                        <p className="text-xs text-slate-500">Thời gian</p>
                        <p className="font-bold text-slate-900">
                          {formatDuration(getDurationSeconds(item))}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs text-slate-500">Trạng thái</p>
                        <p className="font-medium text-slate-800">
                          {item.status === "timeout"
                            ? "Hết giờ"
                            : item.status === "incomplete"
                            ? "Nộp thiếu"
                            : "Hoàn thành"}
                        </p>
                      </div>

                      <div className="flex items-center lg:justify-end">
                        {item.reviewable !== false && item.testId && (
                          <a
                            href={`/ket-qua/${item.testId}?historyId=${item.id}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 rounded-xl bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                          >
                            Xem
                            <FiExternalLink />
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
